/**
 * Centralized formatting utilities adhering to workspace rules:
 * - Strict 24-hour time formatting across all time displays.
 * - 5-character lowercase alphanumeric booking IDs.
 * - Standard Indian Rupee (INR) currency formatting.
 */

/**
 * Format currency with 2 decimal places in Indian numbering format (e.g. ₹1,499.00)
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2
  }).format(amount || 0);
};

/**
 * Format price without decimal places in Indian numbering format (e.g. ₹1,499)
 */
export const formatPrice = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

/**
 * Format date in standard British date format (e.g. 24 Sep 2026)
 */
export const formatDate = (dateInput) => {
  if (!dateInput) return '—';
  try {
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return '—';
  }
};

/**
 * Format date to clean 2-digit day format (e.g. 24 Sep 2026)
 */
export const formatDisplayDate = (dateInput) => {
  if (!dateInput) return '';
  try {
    const cleanDate = String(dateInput).replace(/(\d+)(st|nd|rd|th)/g, '$1');
    const d = new Date(cleanDate);
    if (isNaN(d.getTime())) return String(dateInput);
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return String(dateInput);
  }
};

/**
 * Format 24-hour time string (e.g. 14:30)
 * Works with Date objects, ISO strings, or 12h/24h time strings.
 */
export const format24HourTime = (timeInput) => {
  if (!timeInput) return '';

  // If already Date object or ISO timestamp string with Date info
  if (timeInput instanceof Date || (typeof timeInput === 'string' && (timeInput.includes('T') || timeInput.includes('-') || timeInput.includes('/')))) {
    try {
      const d = new Date(timeInput);
      if (!isNaN(d.getTime())) {
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
      }
    } catch {
      // Fall through to string parser
    }
  }

  // Handle strings like "11:00 AM - 12:00 PM" or "10:00 AM – 11:00 AM"
  const str = String(timeInput).trim();
  const rangeDelimiters = [' - ', ' – ', ' to '];
  for (const delimiter of rangeDelimiters) {
    if (str.includes(delimiter)) {
      const parts = str.split(delimiter);
      return parts.map((p) => formatSingleTimeTo24Hour(p.trim())).join(' - ');
    }
  }

  return formatSingleTimeTo24Hour(str);
};

/**
 * Helper to convert single time string to strict 24-hour format
 */
const formatSingleTimeTo24Hour = (timeStr) => {
  if (!timeStr) return '';
  const clean = timeStr.trim();
  const match = clean.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!match) return clean;

  let [, hoursStr, minutes, , modifier] = match;
  let hours = parseInt(hoursStr, 10);

  if (modifier) {
    const mod = modifier.toUpperCase();
    if (mod === 'PM' && hours < 12) hours += 12;
    if (mod === 'AM' && hours === 12) hours = 0;
  }

  const hh = String(hours).padStart(2, '0');
  return `${hh}:${minutes}`;
};

/**
 * Format date & 24-hour time combined (e.g. 24 Sep 2026 • 14:30)
 */
export const format24HourDateTime = (dateInput) => {
  if (!dateInput) return '—';
  try {
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return String(dateInput);
    const datePart = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const timePart = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${datePart} • ${timePart}`;
  } catch {
    return String(dateInput);
  }
};

/**
 * Strict 5-character lowercase alphanumeric booking ID formatter
 */
export const formatBookingId = (bookingId) => {
  if (!bookingId) return '';
  return String(bookingId).toLowerCase().trim().slice(0, 5);
};

/**
 * Relative time ago string (e.g. Just now, 5m ago, 2h ago, 24 Sep)
 */
export const getTimeAgo = (dateInput) => {
  if (!dateInput) return 'Never';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return 'Never';

  const seconds = Math.floor((new Date() - date) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
};
